import type { QueryParams, Store, DirectorRouter } from 'mobx-router'
import { RouterStore, startRouter } from 'mobx-router'
import { routes, buildUrl } from '../config/routes'
import { computed, makeObservable, observable, runInAction } from 'mobx'
import type { ImpersonationInfo } from '@breakoutlearning/firebase-repository/types'
import { BreakoutUser } from '@breakoutlearning/firebase-repository/models/BreakoutUser'
import type { FirebaseRepository } from '@breakoutlearning/firebase-repository/models/FirebaseRepository'

export class RootStore {
  public router: RouterStore<Store>
  public directorRouter: DirectorRouter

  @observable
  impersonationInfo: ImpersonationInfo | undefined

  @observable
  private _impersonatedBreakoutUser: BreakoutUser | undefined

  constructor() {
    this.router = new RouterStore<Store>(this)
    this.impersonationInfo = undefined

    this.loadImpersonationInfo()

    this.startRouter()

    makeObservable(this)
  }

  startRouter() {
    try {
      const hashValue = window.location.hash
      // the Director does not like hash values here, it will mess with them
      // So we clear the hash value before starting the router
      // clear the hash value if it exists
      if (hashValue) window.location.hash = ''
      this.directorRouter = startRouter(routes, this, {
        html5history: true,
      })
      // restore the hash value if it exists
      if (hashValue) window.location.hash = hashValue
    } catch (e) {
      console.error(e)
    }
  }

  get hasStartedRouter() {
    return !!this.directorRouter
  }

  @computed
  get queryParams() {
    return this.router.queryParams || {}
  }

  navigateToUrl(url: string) {
    window.history.pushState({}, '', url)
    this.directorRouter.handler()
  }

  navigateTo = (
    routeName: string,
    paramsObj?: QueryParams,
    queryParams?: QueryParams
  ) => {
    const route = routes[routeName]
    if (!route) {
      throw new Error(`Route with name ${routeName} not found`)
    }

    // a11y requirement - any navigation needs to reset focus to top of page
    document.getElementById('top-anchor')?.focus()

    this.router.goTo(route, paramsObj, queryParams)
  }

  navigateToWithHook(
    routeName: string,
    {
      hook,
      params,
    }: {
      hook: () => void
      params?: QueryParams
    }
  ) {
    const route = routes[routeName]
    if (!route) {
      throw new Error(`Route with name ${routeName} not found`)
    }

    this.router.goTo(route, params)
    window.requestAnimationFrame(hook)
  }

  navigateToAdminSlideDeck(slideDeckId: string, queryParams?: QueryParams) {
    this.navigateTo('adminSlideDeck', { slideDeckId }, queryParams)
  }

  navigateToMeeting(roomId: string, queryParams?: QueryParams) {
    this.navigateTo('meeting', { roomId }, queryParams)
  }

  navigateToDemoWaitingRoom(roomId: string, queryParams?: QueryParams) {
    this.navigateTo('demoWaitingRoom', { roomId }, queryParams)
  }

  navigateToInstructorClassesPage(queryParams?: QueryParams) {
    this.navigateTo('instructorClasses', queryParams)
  }

  navigateToInstructorCatalogPage(queryParams?: QueryParams) {
    this.navigateTo('instructorLibrary', undefined, queryParams)
  }

  navigateToInstructorClassPage(sectionId: string, queryParams?: QueryParams) {
    this.navigateTo('instructorClass', { id: sectionId }, queryParams)
  }

  navigateToStudentLibraryPage(queryParams?: QueryParams) {
    this.navigateTo('home', queryParams)
  }

  navigateToLtiErrorPage(errorText: string) {
    this.navigateTo('ltiError', undefined, {
      text: errorText,
    })
  }

  navigateToInstructorAssignmentPage(
    sectionId: string,
    assignmentId: string,
    queryParams?: QueryParams
  ) {
    this.navigateTo(
      'instructorClassAssignment',
      {
        id: sectionId,
        assignmentId,
      },
      queryParams
    )
  }

  navigateToAdminTeachingPlan(
    teachingPlanId: string,
    catalogId: string,
    queryParams?: QueryParams
  ) {
    this.navigateTo(
      'adminTeachingPlan',
      { catalogId, nextbookId: teachingPlanId },
      queryParams
    )
  }

  navigateToAdminCatalog(catalogId: string, queryParams?: QueryParams) {
    this.navigateTo('adminCatalog', { catalogId }, queryParams)
  }

  navigateToAdminCatalogs(queryParams?: QueryParams) {
    this.navigateTo('adminCatalogs', undefined, queryParams)
  }

  /**
   * Update the query params of the current route.
   *
   * This is useful for updating the query params of the current route without changing the route.
   *
   * It also means you don't have to know anything about the current route, you just the query params you want to update.
   *
   * The update merges the new query params with the existing query params.
   *
   * This can only be done on routes that have a title (titles are specified in routes list)
   *
   */
  updateQueryParams(
    queryParams: QueryParams,
    options?: { replaceHistory?: boolean }
  ) {
    const route = this.router.currentRoute
    // this should generally not be possible, but in type-land it is
    if (!route) return
    if (!route.title) {
      console.error('updateQueryParams can only be called on a named route')

      return
    }

    const newQueryParams = {
      ...this.router.queryParams,
      ...queryParams,
    }

    // This relies on a internal knowledge how mobx-router works
    // It uses autorun to pushState, but only if the paths are different
    //
    // So to enable replacing history, we manually replace the history first
    // So that autorun exits early
    if (options?.replaceHistory) {
      // Keys sorted alphabetically to match mobx-router's serialization,
      // which uses query-string with its default `sort: true`. If the two
      // disagree the autorun in start-router.ts sees a "different" path
      // and force-pushes anyway, defeating the replace.
      const sortedQueryParams = Object.fromEntries(
        Object.entries(newQueryParams).sort(([a], [b]) => a.localeCompare(b))
      )
      const newPath = buildUrl(
        route.title,
        this.router.params,
        sortedQueryParams
      )

      window.history.replaceState({}, '', newPath)
    }

    // Even though we are not really navigating, we still want to trigger a
    // state update in mobx.
    this.router.goTo(route, this.router.params, newQueryParams)
  }

  navigateToSlideDeckPage(slideDeckId: string, queryParams?: QueryParams) {
    this.navigateTo(
      'instructorSlideDeck',
      {
        slideDeckId,
      },
      { ...queryParams }
    )
  }

  getSlideDeckPageUrl(slideDeckId: string, queryParams?: QueryParams) {
    return buildUrl(
      'instructorSlideDeck',
      {
        slideDeckId,
      },
      queryParams
    )
  }

  navigateToTeachingPlanPage(
    catalogId: string,
    teachingPlanId: string,
    queryParams?: QueryParams
  ) {
    return this.navigateTo(
      'instructorTeachingPlan',
      {
        catalogId: catalogId,
        nextbookId: teachingPlanId,
      },
      queryParams
    )
  }

  navigateToAdminDashboard(tab: string) {
    this.navigateTo('adminDashboard', undefined, {
      tab,
    })
  }

  navigateToAdminInspectorRoom(roomId: string, tab?: string) {
    this.navigateTo(
      'adminInspectorRoom',
      { roomId },
      {
        tab,
      }
    )
  }

  navigateToInstructorAssistants() {
    this.navigateTo('instructorAssistants')
  }

  navigateToInstructorAssistantsInvitePage() {
    this.navigateTo('instructorAssistantsInvite')
  }

  navigateToInstructorAssistantsConfigureAssistantPage(assistantId: string) {
    this.navigateTo(
      'instructorAssistantsConfigureAssistant',
      { id: assistantId },
      {}
    )
  }

  navigateToInstructorAssistantsConfigureInvitationPage(invitationId: string) {
    this.navigateTo(
      'instructorAssistantsConfigureInvitation',
      { id: invitationId },
      {}
    )
  }

  navigateToHome(queryParams?: QueryParams) {
    this.navigateTo('home', {}, queryParams)
  }

  impersonatedBreakoutUser(repository: FirebaseRepository): BreakoutUser {
    if (!this.impersonationInfo) return repository.breakoutUser! // Repository is always initialized but doesn't communicate that in its type

    if (
      this._impersonatedBreakoutUser &&
      this._impersonatedBreakoutUser.uid === this.impersonationInfo.userId
    )
      return this._impersonatedBreakoutUser

    const impersonatedUser = new BreakoutUser(
      repository,
      this.impersonationInfo.userId
    )

    impersonatedUser.initialize()

    this.setImpersonatedBreakoutUser(impersonatedUser)

    return impersonatedUser
  }

  private setImpersonatedBreakoutUser(user: BreakoutUser | undefined) {
    runInAction(() => {
      if (this._impersonatedBreakoutUser) {
        this._impersonatedBreakoutUser.dispose()
      }
      this._impersonatedBreakoutUser = user
    })
  }

  impersonateUser({
    userId,
    organizationId,
    hideBanner,
  }: {
    userId: string
    organizationId?: string
    hideBanner?: boolean
  }) {
    if (!userId) return // don't allow empty userId
    runInAction(() => {
      sessionStorage.setItem('impersonateUserId', userId)
      if (organizationId) {
        sessionStorage.setItem('impersonateOrganizationId', organizationId)
      }
      this.impersonationInfo = { userId, organizationId, hideBanner }
      this.setImpersonatedBreakoutUser(undefined)
    })
  }

  stopImpersonation() {
    runInAction(() => {
      sessionStorage.removeItem('impersonateUserId')
      sessionStorage.removeItem('impersonateOrganizationId')
      this.impersonationInfo = undefined
      this.setImpersonatedBreakoutUser(undefined)
    })
  }

  loadImpersonationInfo() {
    const userId = sessionStorage.getItem('impersonateUserId')
    const organizationId =
      sessionStorage.getItem('impersonateOrganizationId') || undefined
    if (userId) {
      this.impersonationInfo = { userId, organizationId }
      this.setImpersonatedBreakoutUser(undefined)
    }
  }

  navigateToPodcastsPage(queryParams?: QueryParams) {
    this.navigateTo('settings', undefined, {
      ...queryParams,
      tab: 'podcast',
    })
  }

  navigateToStudentAssignment(
    sectionId: string,
    assignmentId: string,
    roomStateId?: string,
    queryParams?: QueryParams
  ) {
    if (roomStateId) {
      this.navigateTo(
        'assignmentWithRoom',
        {
          sectionId,
          assignmentId,
          roomStateId,
        },
        { ...queryParams }
      )
    } else {
      this.navigateTo(
        'assignment',
        {
          sectionId,
          assignmentId,
        },
        { ...queryParams }
      )
    }
  }
}
